import { useScrollReveal } from "@/hooks/useScrollReveal";
import { practiceAreas, engagementPaths } from "@/mocks/homeData";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

function SectionReveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Services() {
  const buyerProblems = [
    {
      text: "You're modernising your technology estate and need every migration to land cleanly.",
      label: "M1 · Modernize",
    },
    {
      text: "Your workforce needs to work from anywhere securely, and your data needs to work smarter.",
      label: "M2 · Mobilize & Magnify",
    },
    {
      text: "You want technology that keeps running, improving, and adapting after go-live.",
      label: "M3 · Manage Success",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Technology Consulting & IT Services USA — Cloud Migration & Managed IT
          | Azosis
        </title>

        <meta
          name="description"
          content="Azosis Technologies USA delivers technology consulting, cloud migration, and managed IT services to enterprises across the United States. Incorporated in Delaware with global delivery capability."
        />

        <meta
          name="keywords"
          content="technology consulting USA, IT consulting United States, cloud migration USA, managed IT services USA, digital transformation USA, Azosis USA"
        />

        <link rel="canonical" href="https://azosis.us/services/" />

        <meta
          property="og:title"
          content="Technology Consulting & IT Services USA | Azosis Technologies"
        />

        <meta
          property="og:description"
          content="Cloud migration, workspace modernisation, and managed IT services for enterprises across the USA."
        />

        <meta property="og:type" content="website" />

        <meta property="og:url" content="https://azosis.us/services/" />

        <meta
          property="og:image"
          content="https://azosis.us/assets/azosis_og_image.png"
        />

        <meta property="og:site_name" content="Azosis Technologies" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Technology Consulting & IT Services USA | Azosis"
        />

        <meta
          name="twitter:description"
          content="Cloud migration, workspace modernisation, and managed IT services for enterprises across the United States."
        />

        <meta
          name="twitter:image"
          content="https://azosis.us/assets/azosis_og_image.png"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="bg-azosis-dark min-h-screen pt-20">
        {/* S1 — Hero */}
        <section className="py-8 md:py-12 bg-azosis-surface">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-4xl mx-auto text-center">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                Azosis Services
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Cloud migration, digital workspace, and managed IT services -
                built around your business.
              </h1>
              <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-10">
                Migration, workspace modernisation, and managed operations - 42
                services, three practice areas, every engagement starting with
                your problem.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
              >
                Book a discovery workshop
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* S3-S5 — Practice Areas Tabs */}
        <section className="py-8 md:py-12 bg-azosis-dark">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <SectionReveal>
                <div className="text-center mb-16">
                  <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                    Practice Areas
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                    Three Practice Areas. One Integrated Program.
                  </h2>
                </div>
              </SectionReveal>

              {/* Stacked Practice Areas */}
              <div className="space-y-8">
                {practiceAreas.map((area, index) => (
                  <SectionReveal key={area.id} delay={index * 150}>
                    <div className="bg-azosis-surface border border-azosis-border">
                      <div className="p-8 md:p-12 border-b border-azosis-border">
                        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                          <span className="text-azosis-gold font-bold text-xl tracking-wider">
                            {area.id}
                          </span>
                          <span className="text-azosis-textMuted text-sm">
                            Aligned to {area.alignedTo}
                          </span>
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                          {area.name}
                        </h3>
                        <p className="text-azosis-gold text-base mb-4">
                          {area.tagline}
                        </p>
                        <p className="text-azosis-textSecondary text-base md:text-lg leading-relaxed">
                          {area.description}
                        </p>
                      </div>

                      {"subAreas" in area && Array.isArray(area.subAreas) ? (
                        (
                          area.subAreas as {
                            name: string;
                            services: string[];
                          }[]
                        ).map((subArea) => (
                          <div
                            key={subArea.name}
                            className="p-8 md:p-12 border-b border-azosis-border last:border-b-0"
                          >
                            <h4 className="text-white font-semibold text-sm tracking-wide uppercase mb-6">
                              {subArea.name} ({subArea.services.length}{" "}
                              services)
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {subArea.services.map((service) => (
                                <div
                                  key={service}
                                  className="flex items-center gap-3 px-4 py-3 bg-azosis-dark border border-azosis-border hover:border-azosis-gold/30 transition-colors"
                                >
                                  <i className="ri-check-line text-azosis-gold text-sm flex-shrink-0"></i>
                                  <span className="text-azosis-textSecondary text-sm">
                                    {service}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 md:p-12">
                          <h4 className="text-white font-semibold text-sm tracking-wide uppercase mb-6">
                            Services ({area.services.length})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {area.services.map((service) => (
                              <div
                                key={service}
                                className="flex items-center gap-3 px-4 py-3 bg-azosis-dark border border-azosis-border hover:border-azosis-gold/30 transition-colors"
                              >
                                <i className="ri-check-line text-azosis-gold text-sm flex-shrink-0"></i>
                                <span className="text-azosis-textSecondary text-sm">
                                  {service}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* S6 — Engagement Paths */}
        <section className="py-8 md:py-12 bg-azosis-dark">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-5xl mx-auto">
              <SectionReveal>
                <div className="text-center mb-16">
                  <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                    How to Engage
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                    Three ways to begin
                  </h2>
                  <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto">
                    Sized to match how ready you are to commit.
                  </p>
                </div>
              </SectionReveal>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {engagementPaths.map((path, index) => (
                  <SectionReveal key={path.step} delay={index * 150}>
                    <div className="bg-azosis-surface border border-azosis-border p-8 md:p-10 h-full flex flex-col">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="w-10 h-10 bg-azosis-gold text-azosis-dark font-bold text-sm flex items-center justify-center">
                          {path.step}
                        </span>
                        <span className="text-azosis-textMuted text-xs uppercase tracking-wide">
                          {path.for}
                        </span>
                      </div>
                      <h3 className="text-white font-display text-xl font-bold mb-2">
                        {path.title}
                      </h3>
                      <p className="text-azosis-gold text-sm mb-4">
                        {path.duration}
                      </p>
                      <p className="text-azosis-textSecondary text-sm leading-relaxed flex-1">
                        {path.description}
                      </p>
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* S7 — Bottom CTA */}
        <section className="py-8 md:py-12 bg-azosis-surface relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-azosis-gold/20 rounded-full blur-[150px]"></div>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <SectionReveal>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                  Ready to begin?
                </h2>
                <p className="text-azosis-textSecondary text-base md:text-lg leading-relaxed mb-10">
                  Every engagement starts with a conversation.
                </p>
                <div className="flex items-center justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
                  >
                    Book a discovery workshop
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
